import React from 'react';
import { i18n } from 'config/locales';

import { Summary, DateSummary } from 'components/Summary';
import ErrorMessageList from 'components/ErrorMessageList';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';

import { CITIZENSHIP, CITIZENSHIP_MULTIPLE } from 'config/formSections/citizenship';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import Comments from 'components/Form/Comments';
import { countryString } from '../../../../validators/location';
import CitizenshipItem from './CitizenshipItem';

const sectionConfig = {
  key: CITIZENSHIP_MULTIPLE.key,
  section: CITIZENSHIP.name,
  store: CITIZENSHIP.store,
  subsection: CITIZENSHIP_MULTIPLE.name,
  storeKey: CITIZENSHIP_MULTIPLE.storeKey,
};

export class Multiple extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;

    this.state = {
      uid: `${super.guid()}`,
    };
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      HasMultiple: this.props.HasMultiple,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...queue,
    });
  };

  updateHasMultiple = (values) => {
    const isYes = values.value === 'Yes';
    this.update({
      HasMultiple: values,
      List: isYes ? this.props.List : [],
      SummaryComments: isYes ? this.props.SummaryComments : {},
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summaryList = (item, index) => {
    const isPVQ = isPVQForm(this.props.formType);
    const itemProperties = (item || {}).Item || {};
    const dates = DateSummary(itemProperties.Dates);
    const country = isPVQ ? (itemProperties.Country?.value?.join?.(', ') || '') : (countryString(itemProperties.Country) || '');
    return Summary({
      type: i18n.t('citizenship.multiple.collection.citizenship.summary.item'),
      index,
      left: country,
      right: dates,
      placeholder: i18n.t('citizenship.multiple.collection.citizenship.summary.unknown'),
    });
  };

  /**
   * Reads formErrors for List errors related to accordion and returns accordion errors
   * used for rendering error message in the component
   * @returns {[]}
   */
  getSectionErrors = () => {
    const { formErrors } = this.props;
    const errorItem = {
      key: 'List.accordion.items.length.error.list.length.min',
      title: i18n.t('error.validMinimumCitizenships.title'),
      message: i18n.t('error.validMinimumCitizenships.message'),
      shouldDisplayError: true,
    };

    const sectionErrors = [];
    formErrors.forEach((error) => {
      if (error.attribute === 'List.items' && error.code === 'error.list.length.min') {
        sectionErrors.push(errorItem);
      }
    });

    return sectionErrors;
  };

  render() {
    const {
      List, errors, formType, formVersion
    } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const isPVQ = isPVQForm(this.props.formType);
    const branchAttribute = 'HasMultiple';

    return isPVQ ? (
      <div
        className="section-content multiple"
        data-section={CITIZENSHIP.key}
        data-subsection={CITIZENSHIP_MULTIPLE.key}
      >
        <h1 className="section-header">{i18n.t('citizenship.destination.additional')}</h1>
        <h4 className="bottom-spacing">{i18n.t('citizenship.pvq.additional.heading.subtitle')}</h4>
        <Branch
          attribute={branchAttribute}
          name="has_multiple"
          label={i18n.t('citizenship.pvq.additional.heading.hasadditional')}
          labelSize="h4"
          className="has-multiple"
          {...this.props.HasMultiple}
          warning
          onUpdate={this.updateHasMultiple}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.HasMultiple.value === 'Yes' && (
          <>{List.branch && List.branch.value === 'No' && (
            <div className="field accordionfield" data-uuid={this.state.uid}>
              <span id={this.state.uid} className="anchor" aria-hidden="true" />

              <h4 className="title h4 hidden">Provide a minimum of two countries.</h4>
              <ErrorMessageList errors={this.getSectionErrors()} />
            </div>
          )}

            <Accordion
              {...this.props.List}
              defaultState={this.props.defaultState}
              scrollToBottom={this.props.scrollToBottom}
              onUpdate={this.updateList}
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              errors={accordionErrors}
              summary={this.summaryList}
              description={i18n.t('citizenship.pvq.additional.collection.citizenship.summary.title')}
              appendTitle={i18n.t('citizenship.pvq.additional.collection.citizenship.appendTitle')}
              appendLabel={i18n.t('citizenship.pvq.additional.collection.citizenship.append')}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            >
              <CitizenshipItem
                name="Item"
                bind
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                formType={formType}
                formVersion={formVersion}
              />
            </Accordion>
          </>
        )}
      </div>
    ) : (
      <div
        className="section-content multiple"
        data-section={CITIZENSHIP.key}
        data-subsection={CITIZENSHIP_MULTIPLE.key}
      >
        <h1 className="section-header">{i18n.t('citizenship.destination.multiple')}</h1>
        <Branch
          attribute={branchAttribute}
          name="has_multiple"
          label={i18n.t('citizenship.multiple.heading.hasmultiple')}
          labelSize="h4"
          className="has-multiple"
          {...this.props.HasMultiple}
          warning
          onUpdate={this.updateHasMultiple}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('citizenship.comments.branch')}
            name={`${branchAttribute}Comments`}
            className="branch-comment"
            attribute={`${branchAttribute}Comments`}
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        )}

        <Show when={this.props.HasMultiple.value === 'Yes'}>
          {List.branch && List.branch.value === 'No' && (
            <div className="field accordionfield" data-uuid={this.state.uid}>
              <span id={this.state.uid} className="anchor" aria-hidden="true" />

              <h4 className="title h4 hidden">Provide a minimum of two countries.</h4>
              <ErrorMessageList errors={this.getSectionErrors()} />
            </div>
          )}

          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            summary={this.summaryList}
            description={i18n.t('citizenship.multiple.collection.citizenship.summary.title')}
            appendTitle={i18n.t('citizenship.multiple.collection.citizenship.appendTitle')}
            appendLabel={i18n.t('citizenship.multiple.collection.citizenship.append')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            minItems={2}
          >
            <CitizenshipItem
              name="Item"
              bind
              requireMultipleCitizenshipRenounced={this.props.requireMultipleCitizenshipRenounced}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={formType}
              formVerison={formVersion}
            />
          </Accordion>
          {!isFormSF852013(formType, formVersion) && (
            <Comments
              title={i18n.t('citizenship.comments.summary')}
              name={`${branchAttribute}SummaryComments`}
              className="optional-summary-comment"
              attribute={`${branchAttribute}SummaryComments`}
              {...this.props.SummaryComments}
              onUpdate={this.updateSummaryComments}
              required={false}
            />
          )}
        </Show>
      </div>
    );
  }
}

Multiple.defaultProps = {
  HasMultiple: {},
  Comments: {},
  SummaryComments: {},
  List: { items: [], branch: {} },
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  defaultState: true,
  required: false,
  scrollIntoView: false,
  requireMultipleCitizenshipRenounced: true,
  errors: [],
};

export default connectSubsection(Multiple, sectionConfig);
